// ═══════════════════════════════════════════════════════════════
// tabsEmpresa — estructura de tabs/sub-tabs de la página de detalle de empresa.
//
// Fuente ÚNICA: la usa `EmpresaDetailPage` para renderizar y `gestion/config.js`
// para declarar sus "vistas gateables" (VISTAS_GESTION) al sistema de
// habilitación (core/lib/vistasEmpresa). Antes vivía inline en la página.
// ═══════════════════════════════════════════════════════════════

// ─── Tab groups: grupos principales con sub-tabs ───
export const TAB_GROUPS = [
  {
    id: 'trabajo',
    label: 'Trabajo',
    icon: 'trabajo',
    tabs: [
      { id: 'agenda-trabajo', label: 'Agenda' },
      { id: 'tareas', label: 'Tareas' },
      { id: 'ciclos', label: 'Ciclos' },
      { id: 'reuniones', label: 'Actividades' },
      { id: 'casos', label: 'Casos' },
      // PR-1b Reportes congelados (delta §1-bis.4): ESPEJO de Empresa→Reportes
      // (mismo componente, misma colección) con filtro Pendientes por default —
      // la cola de aprobación del consultor vive donde vive su trabajo.
      { id: 'reportes-trabajo', label: 'Reportes' },
    ],
  },
  {
    id: 'empresa',
    label: 'Empresa',
    icon: 'empresa',
    tabs: [
      { id: 'datos', label: 'Datos' },
      { id: 'areas', label: 'Áreas' },
      { id: 'equipo', label: 'Equipo' },
      { id: 'organigrama', label: 'Organigrama' },
      { id: 'modulos', label: 'Módulos' },
      { id: 'agentes', label: 'Agentes' },
      { id: 'productos', label: 'Productos' },
      { id: 'impuestos', label: 'Impuestos' },
      { id: 'aprendizajes', label: 'Aprendizajes' },
      { id: 'reglas-decision', label: 'Reglas de decisión' },
      { id: 'consumo-nube-ia', label: 'Consumo Nube e IA' },
      { id: 'reportes-generados', label: 'Reportes' },
      // Report iZCZBwG0 (8-jul-2026): la solapa "Sincronización" (sync-override)
      // se sacó de la navegación por decisión de producto (Mariano): resincronizar
      // o forzar se hace desde la barra de sync del endpoint que se necesita, no
      // desde la ficha. Entonces quedaron vivos el componente y su deep-link.
      //
      // 15-ago-2026: **el componente se BORRÓ**. Su único botón era "⚡ Forzar
      // webhook", y el ADR `2026-08-15-el-override-de-sync-solo-puede-restringir-
      // lo-que-el-plan-permite` eliminó la ampliación per-empresa: si un cliente
      // necesita más de lo que su plan da, se le cambia el plan (Mariano). Dejar
      // la solapa habría sido ofrecer un botón que el servidor rechaza.
    ],
  },
  {
    id: 'diagnostico',
    label: 'Diagnóstico',
    icon: 'diagnostico',
    tabs: [
      { id: 'pulso', label: 'Pulso' },
      { id: 'foda', label: 'FODA' },
      { id: 'mercado', label: 'Mercado' },
      { id: 'dimensiones', label: 'Dimensiones' },
      { id: 'gestion-warnings', label: 'Warnings' },
      { id: 'problematicas', label: 'Problemáticas' },
    ],
  },
  {
    id: 'documentos',
    label: 'Documentos',
    icon: 'documentos',
    tabs: [
      { id: 'bitacora', label: 'Bitácora' },
      { id: 'reportes', label: 'Documentos' },
    ],
  },
  {
    // Prospectiva = grupo PROPIO entre Documentos y Dashboards (decisión
    // Mariano 3-ago v2, tras ver la v1 como sub-tab): la visión a futuro no es
    // "un dashboard más", es su propia solapa. Al ser grupo (no 'dashboards'),
    // entra a VISTAS_GESTION → se prende/apaga por rol desde Plataforma
    // Configuración → Vista detallada de empresa, como pidió Mariano.
    id: 'prospectiva',
    label: 'Prospectiva',
    icon: 'prospectiva',
    tabs: [
      { id: 'prospectiva-plan-compras', label: 'Compras a largo plazo' },
    ],
  },
  {
    id: 'dashboards',
    label: 'Dash Mercado Libre',
    icon: 'dashboards',
    tabs: [
      { id: 'dash-mercado-libre', label: 'Mercado Libre' },
    ],
  },
  {
    // F2-T3 (ADR 2026-08-16): grupo del canal Tienda Nube. El sub-tab rutea
    // por el prefijo dash-* de EmpresaDetailPage (plataformaForzada
    // 'tienda-nube'). A diferencia del grupo ML (siempre visible por la
    // excepción de EmpresaDetailPage:207), este grupo se gatea por el nodo
    // 'dash-tienda-nube' que declara el módulo tienda-nube en vistasEmpresa.
    id: 'dashboards-tn',
    label: 'Dash Tienda Nube',
    icon: 'dashboards',
    tabs: [
      { id: 'dash-tienda-nube', label: 'Tiendanube' },
    ],
  },
  {
    // Mi negocio (2-oct-2026): los tableros que miran el negocio entero y
    // comparan canales — el primero, Precios. Rutea por el mismo prefijo dash-*
    // ('mi-negocio' es un ÁMBITO del registro, no un canal). Se gatea por el
    // nodo 'dash-mi-negocio' que declara el módulo mi-negocio, y además sólo
    // aparece si la empresa tiene conectados Mercado Libre Y su tienda
    // (EmpresaDetailPage): con un solo canal no hay nada que comparar.
    id: 'dashboards-mn',
    label: 'Mi negocio',
    icon: 'dashboards',
    // El ámbito del registro de dashboards: dice qué canales tienen que estar
    // conectados (`gruposPorConexiones.js`).
    ambito: 'mi-negocio',
    tabs: [
      { id: 'dash-mi-negocio', label: 'Mi negocio' },
    ],
  },
  {
    // Observabilidad de sincronización per-empresa (solapa Sincro). Host neutral
    // en gestión; cada canal aporta su sub-tab. Gateable por rol vía VISTAS_GESTION.
    id: 'sincro',
    label: 'Sincro',
    icon: 'sincro',
    tabs: [
      { id: 'sincro-mercado-libre', label: 'Mercado Libre' },
    ],
  },
]

// Vistas gateables que aporta gestión al árbol de "Vista detallada de empresa":
// cada SUB-TAB (hoja) de los grupos, EXCEPTO el grupo 'dashboards' (sus hojas
// gateables son las cards, que las declara el módulo mercadolibre). El wrapper
// de canal 'dash-mercado-libre' no es gateable por sí mismo.
export const VISTAS_GESTION = TAB_GROUPS
  // Los grupos de dashboards se excluyen: sus hojas gateables (cards y, en el
  // caso TN y Mi negocio, también el nodo del tab) las declara el módulo dueño.
  .filter(g => g.id !== 'dashboards' && g.id !== 'dashboards-tn' && g.id !== 'dashboards-mn')
  .flatMap((g, gi) => g.tabs.map((t, ti) => ({
    id: t.id,
    label: t.label,
    grupo: g.id,
    grupoLabel: g.label,
    grupoOrden: gi,
    orden: ti,
  })))
